import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {Formik} from 'formik';
import {isEqual} from 'lodash';
import moment from 'moment';
import {useAutomatedPaymentsUIContext} from '../AutomatedPaymentsUIContext';
import {FilterFromDate, FilterToDate} from '../../../_common/components/FilterDateRange';
import FilterTextField from '../../../_common/components/FilterTextField';
import FilterSelect from '../../../_common/components/FilterSelect';
import ButtonExportToExcel from '../../../_common/components/ButtonExportToExcel';
import {ButtonUploadExcel} from '../../../_common/components/ButtonUploadExcel/ButtonUploadExcel';
import FiltersBar from '../../../_common/components/FiltersBar';
import * as actions from '../../_redux/paymentsActions';
import {Dropdown} from 'react-bootstrap';
import {useDispatch} from 'react-redux';
import {generateExcelFile} from '../../../_common/utils/excel';
import {headersBituahLeumi} from '../../../_common/constants/exportedFilesHeaders';
import {bituahLeumiHeader} from '../../../_common/constants/exportedFilesMainHeaders';
import {RequestCancellationManager} from '../../../_common/utils/RequestCancellationManager';
import styles from '../../../Users/pages/users-filter/styles.module.scss';
import ButtonSmartFilter from '../../../_common/components/ButtonSmartFilter';

const prepareFilter = (queryParams, values) => {
  const {agentID, name, fromDate, toDate, amount, statusID, idNumber} = values;
  const newQueryParams = {...queryParams};
  const filter = {};
  if (Boolean(agentID)) {
    filter.agentID = agentID;
  } else {
    delete newQueryParams.agentID;
  }
  if (Boolean(name)) {
    filter.name = name;
  } else {
    delete newQueryParams.name;
  }
  if (Boolean(fromDate)) {
    filter.fromDate = moment(fromDate).format('YYYY-MM-DD');
  } else {
    delete newQueryParams.fromDate;
  }
  if (Boolean(toDate)) {
    filter.toDate = moment(toDate).add(1, 'days').format('YYYY-MM-DD');
  } else {
    delete newQueryParams.toDate;
  }
  if (Boolean(amount)) {
    filter.amount = amount;
  } else {
    delete newQueryParams.amount;
  }
  if (Boolean(statusID)) {
    filter.statusID = statusID;
  } else {
    delete newQueryParams.statusID;
  }
  if (Boolean(idNumber)) {
    filter.idNumber = idNumber;
  } else {
    delete newQueryParams.idNumber;
  }
  return {...newQueryParams, ...filter};
};

const CustomDDItem = React.forwardRef(
  ({children, style, className, 'aria-labelledby': labeledBy}, ref) => (
    <div
      className={`${className} d-flex align-items-center pb-0 mb-0`}
      style={style}
      ref={ref}
    >
      {children}
    </div>
  )
);

export function AutomatedPaymentsFilter() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const paymentsUIContext = useAutomatedPaymentsUIContext();
  const paymentsUIProps = useMemo(() => {
    return {
      queryParams: paymentsUIContext.queryParams,
      setQueryParams: paymentsUIContext.setQueryParams,
    };
  }, [paymentsUIContext]);

  const applyFilter = (values) => {
    RequestCancellationManager.stopRequests(actions.getPaymentsListIDPrefix);

    const newQueryParams = prepareFilter(paymentsUIProps.queryParams, values);
    if (!isEqual(newQueryParams, paymentsUIProps.queryParams)) {
      paymentsUIProps.setQueryParams(newQueryParams);
    }
  };

  const prefix = 'automatedPayments-filter';

  const options = [
    {value: '1', label: intl.formatMessage({id: `${prefix}-statusID-new`, defaultMessage: 'New',})},
    {value: '2', label: intl.formatMessage({id: `${prefix}-statusID-done`, defaultMessage: 'Done',})},
    {value: '3', label: intl.formatMessage({id: `${prefix}-statusID-failed`, defaultMessage: 'Failed',})},
    {value: '4', label: intl.formatMessage({id: `${prefix}-statusID-manualDone`, defaultMessage: 'Manual done',})},
    {value: '5', label: intl.formatMessage({id: `${prefix}-statusID-resent`, defaultMessage: 'Resent',})},
    {value: '6', label: intl.formatMessage({id: `${prefix}-statusID-agentNotFound`, defaultMessage: 'Agent not found',})},
    {value: '7', label: intl.formatMessage({id: `${prefix}-statusID-multiAgentFound`, defaultMessage: 'Multi agent found',})},
    {value: '8', label: intl.formatMessage({id: `${prefix}-statusID-noBirthYear`, defaultMessage: 'No birth year'})},
    {value: '9', label: intl.formatMessage({id: `${prefix}-statusID-nameNotInHebrew`, defaultMessage: 'Name not in hebrew'})},
  ];
  const today = moment();
  const startCurrentMonth = moment().startOf('month');
  const startLastMonth = moment().subtract(1, 'month').startOf('month');
  const endLastMonth = moment().subtract(1, 'month').endOf('month');

  const qs = new URLSearchParams(window.location.search);
  const idNumber = qs.get('idNumber') || '';
  const agentID = qs.get('agentID') || '';
  const name = qs.get('name') || '';
  const fromDate = qs.get('fromDate') || '';
  const toDate = Boolean(qs.get('toDate'))
    ? moment(qs.get('toDate')).subtract(1, 'days').toDate()
    : '';
  const statusID = qs.get('statusID') || '';
  const amount = qs.get('amount') || '';
  const monthPeriodValue = (toDate && fromDate)
    ? ((moment(toDate).month() === moment(today).month() &&
      (moment(fromDate).month() === moment(startCurrentMonth).month())) ? '1' : '2')
    : '0';

  const [loading, setLoading] = useState(false);

  const exportPaymentsFileFunc = () => {
    setLoading(true);
    dispatch(actions.exportPaymentsFileAction(paymentsUIProps.queryParams))
      .then(prepareDataForExcel)
      .then((data) => {
        generateExcelFile(data, 'BituahLeumi.xlsx', bituahLeumiHeader)
      })
      .catch((error) => {
        console.error(`Error: + ${error}`);
        setLoading(false);
      })
      .finally(() => setLoading(false));
  };
  const prepareDataForExcel = data => {
    return data.map((item, index) => {
      const {
        agentID,
        firstName,
        lastName,
        idNumber,
        paymentRequestDate,
        totalAmount,
        statusText,
      } = item;

      return {
        [headersBituahLeumi[0]]: index + 1,
        [headersBituahLeumi[1]]: agentID,
        [headersBituahLeumi[2]]: firstName,
        [headersBituahLeumi[3]]: lastName,
        [headersBituahLeumi[4]]: idNumber,
        [headersBituahLeumi[5]]: totalAmount,
        [headersBituahLeumi[6]]: moment(paymentRequestDate).format('DD.MM.YYYY'),
        [headersBituahLeumi[7]]: statusText,
      }
    });
  };

  const smartFilterMonthArray = [
    {id: 'all', monthPeriod: '0', fromMonth: '', toMonth: '', label: intl.formatMessage({id: 'invoices-smart-filter-all', defaultMessage: 'All'})},
    {id: 'thisMonth', monthPeriod: '1', fromMonth: startCurrentMonth, toMonth: today, label: intl.formatMessage({id: 'automatedPayments-smart-filter-this-month', defaultMessage: 'This month'})},
    {id: 'lastMonth', monthPeriod: '2', fromMonth: startLastMonth, toMonth: endLastMonth, label: intl.formatMessage({id: 'automatedPayments-smart-filter-last-month', defaultMessage: 'Last month'})},
  ];
  const smartFilterByMonthPeriod = (setFieldValue, item, handleSubmit) => {
    setFieldValue('monthPeriod', item.monthPeriod);
    setFieldValue('fromDate', item.fromMonth);
    setFieldValue('toDate', item.toMonth);
    handleSubmit();
  };

  const containerClassnames = 'w-200px position-relative my-1 px-3';

  return (
    <Formik
      initialValues={{
        idNumber,
        agentID,
        name,
        fromDate,
        toDate,
        statusID,
        amount,
        monthPeriod: monthPeriodValue,
      }}
      onSubmit={applyFilter}
      enableReinitialize={true}
    >
      {({values, ...formikProps}) => (
          <FiltersBar
            filters={
              <div className={'d-flex flex-wrap justify-content-center align-items-center mb-0 w-100'}>
                <FilterTextField
                  fieldName="name"
                  placeholder={intl.formatMessage({id: `${prefix}-name`, defaultMessage: 'Name',})}
                  value={values.name}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterTextField
                  fieldName="agentID"
                  placeholder={intl.formatMessage({id: `${prefix}-agentID`, defaultMessage: 'Agent Id',})}
                  value={values.agentID}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterTextField
                  fieldName="idNumber"
                  placeholder={intl.formatMessage({id: `${prefix}-idNumber`, defaultMessage: 'Id number'})}
                  value={values.idNumber}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterFromDate
                  values={values}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterToDate
                  values={values}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterTextField
                  fieldName="amount"
                  placeholder={intl.formatMessage({id: `${prefix}-amount`, defaultMessage: 'Amount',})}
                  value={values.amount}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterSelect
                  fieldName={'statusID'}
                  valueName={values.statusID}
                  options={options}
                  placeholder={intl.formatMessage({id: `${prefix}-statusID`, defaultMessage: 'StatusID',})}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
              </div>
            }
            buttons={
              <div className="d-flex align-items-center justify-content-between">
                <ButtonSmartFilter
                  header={intl.formatMessage({id: 'invoices-smart-filter-header', defaultMessage: 'Invoices smart filter',})}
                >
                  <Dropdown.Item as={CustomDDItem}>
                    <div className="d-flex">
                      <h7 className={styles.filterName}>
                        {intl.formatMessage({id: 'automatedPayments-smart-filter-month-header', defaultMessage: 'Automated payments month period',})}</h7>
                      <div className='d-flex flex-wrap'>
                        {smartFilterMonthArray.map((item) => {
                          return (
                            <div className="ml-4">
                              <input
                                type="radio"
                                className="hide-checkbox"
                                id={item.id}
                                name="monthPeriod"
                                onBlur={formikProps.handleBlur}
                                checked={values.monthPeriod === item.monthPeriod}
                                onChange={() => {
                                  smartFilterByMonthPeriod(formikProps.setFieldValue, item, formikProps.handleSubmit)
                                }}
                              />
                              <label htmlFor={item.id} className="custom-checkbox">
                                {item.label}
                              </label>
                            </div>
                          )
                        })
                        }
                      </div>
                    </div>
                  </Dropdown.Item>
                </ButtonSmartFilter>
                <ButtonUploadExcel
                  uploadFunc={actions.uploadFileAction}
                />
                <ButtonExportToExcel
                  loading={loading}
                  exportFunc={exportPaymentsFileFunc}
                />
              </div>
            }
          />
      )}
    </Formik>
  );
}
